import React, { useEffect, useRef, useState } from 'react';
import './ShareButton.styles.css';
import { Icons, IconsVariants } from '../../UI';

interface ShareButtonProps {
  questionId: string;
  title?: string;
}

// Copy text to the clipboard. Prefers the async Clipboard API (secure contexts)
// and falls back to a hidden input + execCommand for older mobile browsers.
const copyToClipboard = async (text: string): Promise<void> => {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // fall through to the legacy path
    }
  }
  const input = document.createElement('input');
  input.value = text;
  // Keep it off-screen + readonly so mobile doesn't scroll/jump or pop the keyboard.
  input.setAttribute('readonly', '');
  input.style.position = 'fixed';
  input.style.top = '-9999px';
  input.style.opacity = '0';
  document.body.appendChild(input);
  try {
    input.select();
    // execCommand returns false when the copy was rejected (e.g. strict perms).
    if (!document.execCommand('copy')) throw new Error('copy command was rejected');
  } finally {
    // Always remove the node, even if select()/execCommand throws.
    document.body.removeChild(input);
  }
};

export const ShareButton: React.FC<ShareButtonProps> = ({ questionId, title }) => {
  const [showCopiedNotification, setShowCopiedNotification] = useState<boolean>(false);
  // Bumped on each show so the toast node remounts and its fadeOut animation
  // restarts when Share is clicked again while a previous toast is still up.
  const [toastKey, setToastKey] = useState<number>(0);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear a pending toast timer on unmount so it can't fire on an unmounted component.
  useEffect(() => () => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
  }, []);

  const handleShare = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    // Full, absolute URL (incl. protocol) so pasted/shared links are clickable
    // and the Cloudflare OG function renders the preview. A cache-busting `s`
    // param nudges platforms (WhatsApp etc.) to re-scrape so the preview
    // reflects current vote numbers instead of the first cached scrape.
    const url = `${window.location.origin}/question/${questionId}?s=${Date.now()}`;

    // Native share sheet (mobile / supported browsers) → WhatsApp, etc.
    if (navigator.share) {
      try {
        await navigator.share({ title: title || 'Pinion', url });
        return;
      } catch (err) {
        // User dismissed the sheet → done. Any other failure (unsupported
        // context, NotAllowedError, …) falls through to the copy fallback.
        // Check name (not instanceof) — not every env throws a DOMException.
        if ((err as { name?: string } | null)?.name === 'AbortError') return;
      }
    }

    // Fallback (most desktops, or a failed share): copy the URL and confirm.
    try {
      await copyToClipboard(url);
    } catch {
      // execCommand/Clipboard can throw under locked-down permissions — don't
      // let it bubble as an unhandled rejection from the click handler.
      return;
    }
    setShowCopiedNotification(true);
    setToastKey((k) => k + 1);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setShowCopiedNotification(false), 2000);
  };

  return (
    <div className='share-button-wrapper'>
      <button
        onClick={handleShare}
        className="question-card-actions-buttons"
        aria-label="Share this question"
      >
        <Icons variant={IconsVariants.SHARE_FULL} />
      </button>
      {showCopiedNotification && (
        <div key={toastKey} className="copied-notification" role="status" aria-live="polite">
          Link copied
        </div>
      )}
    </div>
  );
};
